import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { useUpdateWorkshopTaskDueDateMutation } from 'redux/apiSlice/workshopTasks.slice';
import { WORKSHOP_TASK_DATE_FORMAT } from 'data/types/workshopTasks.types';
import { AddCalendarOutline } from 'assets/icon/icon';
import { SDueDateTriggerButton } from './WorkshopTaskDueDateCell.styles';

type WorkshopTaskDueDateCellProps = {
  workshopId: string;
  taskId: string;
  value: string;
  disabled?: boolean;
};

const WorkshopTaskDueDateCell = ({
  workshopId,
  taskId,
  value,
  disabled,
}: WorkshopTaskDueDateCellProps) => {
  // Guard against an unparseable value so the cell never renders "Invalid Date":
  // a malformed/unexpected string falls back to the empty "Add" state.
  const parsedValue = useMemo(() => {
    if (!value) return null;
    const parsed = dayjs(value, WORKSHOP_TASK_DATE_FORMAT);
    return parsed.isValid() ? parsed : null;
  }, [value]);

  const [selected, setSelected] = useState<Dayjs | null>(parsedValue);
  const [isEditing, setIsEditing] = useState(false);
  const [updateDueDate, { isLoading }] = useUpdateWorkshopTaskDueDateMutation();
  const { showToastNotification } = useShowToastNotification();

  useEffect(() => {
    setSelected(parsedValue);
  }, [parsedValue]);

  const handleChange = async (date: Dayjs | null) => {
    setSelected(date);
    setIsEditing(false);
    const formatted = date ? date.format(WORKSHOP_TASK_DATE_FORMAT) : null;
    try {
      await updateDueDate({ workshopId, taskId, dueDate: formatted }).unwrap();
    } catch {
      setSelected(parsedValue);
      showToastNotification({
        message: 'Could not update due date. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  // Empty state: a calendar-icon "Add" trigger matching the Assignee column
  // (Figma) instead of a bare input. Clicking it opens the date picker.
  if (!selected && !isEditing && !disabled) {
    return (
      <SDueDateTriggerButton
        type="text"
        icon={<AddCalendarOutline size={22} />}
        onClick={() => setIsEditing(true)}
        data-testid="task-due-date"
        data-cy="task-due-date"
      >
        Add
      </SDueDateTriggerButton>
    );
  }

  return (
    <DatePicker
      value={selected}
      onChange={handleChange}
      format={WORKSHOP_TASK_DATE_FORMAT}
      placeholder="Add date"
      allowClear
      showToday={false}
      open={isEditing || undefined}
      autoFocus={isEditing}
      onOpenChange={open => {
        if (!open) setIsEditing(false);
      }}
      disabled={disabled || isLoading}
      suffixIcon={null}
      inputReadOnly
      data-testid="task-due-date"
      data-cy="task-due-date"
    />
  );
};

export default WorkshopTaskDueDateCell;
